'use client';

import { useProductList } from '@/components/product-list-context';
import { usePathname } from '@/i18n/navigation';
import {
  queueListItem,
  resetSeenItems,
} from '@/utils/gtm/item-list-impressions';
import { buildListItem } from '@/utils/gtm/item-lists';
import { useLocale } from 'next-intl';
import { useEffect, useRef } from 'react';

// Spec trigger: at least 50% of the card inside the viewport for 2 seconds.
const VISIBLE_RATIO = 0.5;
const DWELL_MS = 2000;

/**
 * Observes one product in a list and, once it has been half visible for the
 * dwell time, hands it to the batcher that pushes `view_item_list`.
 *
 * Does nothing outside a `ProductListProvider` — a card with no list identity
 * has nothing to report.
 *
 * @returns the ref to put on the card's root element
 */
export default function useItemListImpression(product, index = 0) {
  const ref = useRef(null);
  const context = useProductList();
  const locale = useLocale();
  const pathname = usePathname();

  const latest = useRef(null);
  latest.current = {
    product,
    index,
    list: context?.list,
    viewKey: context?.viewKey ?? '',
    locale,
  };

  const listId = context?.list?.item_list_id;
  const viewKey = context?.viewKey ?? '';
  const itemId = String(
    (Array.isArray(product?.sku) ? product?.sku?.at(0) : product?.sku) ??
      product?.objectID ??
      ''
  );

  useEffect(() => {
    const node = ref.current;
    if (
      !node ||
      !itemId ||
      !listId ||
      typeof IntersectionObserver === 'undefined'
    )
      return;

    // Only bounds the seen set; the view key is part of each item's key.
    resetSeenItems(pathname);

    let timer = null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO) {
          if (timer) return;
          timer = setTimeout(() => {
            timer = null;
            const current = latest.current;
            const item = buildListItem(current);
            if (item) queueListItem(current.list, current.viewKey, item);
            observer.disconnect();
          }, DWELL_MS);
          return;
        }
        // Scrolled back out before the dwell time elapsed — no impression.
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: [0, VISIBLE_RATIO, 1] }
    );

    observer.observe(node);

    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
    };
  }, [itemId, listId, viewKey, pathname, index]);

  return ref;
}
